import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { loadCSVData } from './utils/csvLoader';
import { 
  geocodeInput, 
  sortLocationsByDistance,
  formatDistance
} from './utils/geocodingUtils';
import LocationCard from './components/LocationCard';
import LocationDetails from './components/LocationDetails';
import SearchBar from './components/SearchBar';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import './styles/globals.css';

// CARTO basemaps need a valid API key; without one fall back to OpenStreetMap tiles
const CARTO_KEY = process.env.REACT_APP_CARTO_KEY;

// Fix for default markers in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker for user location
const userLocationIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
  className: 'user-location-marker'
});

// Custom X mark close icon component - 44x44px
// Custom X mark close icon component - 22x22px (half of original 44x44px)
const XMarkIcon = ({ onClick, className = "" }) => {
  return (
    <button 
      className={`close-button ${className}`}
      onClick={onClick}
      aria-label="Close"
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        borderRadius: '4px',
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '22px',
        height: '22px',
        flexShrink: 0
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#f0f0f0';
        e.currentTarget.style.transform = 'scale(1.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
        e.currentTarget.style.transform = 'scale(1)';
      }}
      onMouseDown={(e) => {
        e.currentTarget.style.transform = 'scale(0.9)';
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.transform = 'scale(1.1)';
      }}
    >
      <svg 
        width="22" 
        height="22" 
        viewBox="0 0 22 22" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
        style={{
          width: '22px',
          height: '22px'
        }}
      >
        <path 
          d="M16.5 5.5L5.5 16.5M5.5 5.5L16.5 16.5" 
          stroke="#666666" 
          strokeWidth="1.5" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
};

// Helper component for popup image
// Standalone sites (not hosted at Morrisons or Enterprise) are titled by their operating company
const getPopupTitle = (location) => {
  const address = (location.address || '').toLowerCase();
  const isHosted = address.includes('morrisons') || address.includes('enterprise');
  return !isHosted && location.operatingName ? location.operatingName : location.site;
};

const PopupImage = ({ location }) => {
  const [imageError, setImageError] = useState(false);
  const [fallbackError, setFallbackError] = useState(false);
  
  // Generate image path based on site name
  const getLocationImagePath = () => {
    const siteName = location.site.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    return `/${siteName}.png`;
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleFallbackError = () => {
    setFallbackError(true);
  };

  // Show final fallback - a consistent placeholder
  if (imageError && fallbackError) {
    const expectedPath = getLocationImagePath();
    return (
      <div className="popup-image-container">
        <div 
          className="popup-location-image fallback-placeholder"
          style={{
            width: '100%',
            height: '80px',
            backgroundColor: '#f5f5f5',
            border: '1px solid #e0e0e0',
            borderRadius: '6px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#666',
            fontSize: '10px',
            fontWeight: '500',
            padding: '6px',
            textAlign: 'center'
          }}
        >
          <div style={{ marginBottom: '2px' }}>Missing Image:</div>
          <div style={{ fontSize: '9px', color: '#999' }}>/public{expectedPath}</div>
        </div>
      </div>
    );
  }

  // Show fallback image (generic location image)
  if (imageError) {
    return (
      <div className="popup-image-container">
        <img 
          src="/location-placeholder.png"
          alt={`${location.site} Location`}
          className="popup-location-image"
          onError={handleFallbackError}
          style={{
            width: '100%',
            height: '80px',
            objectFit: 'cover',
            borderRadius: '6px'
          }}
        />
      </div>
    );
  }

  // Try location-specific image first
  return (
    <div className="popup-image-container">
      <img 
        src={getLocationImagePath()}
        alt={`${location.site} Location`}
        className="popup-location-image"
        onError={handleImageError}
        style={{
          width: '100%',
          height: '80px',
          objectFit: 'cover',
          borderRadius: '6px'
        }}
      />
    </div>
  );
};

const MapApp = () => {
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const mapRef = useRef();

  useEffect(() => {
    loadLocationData();
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Separate useEffect for filtering - only runs when dependencies change
  useEffect(() => {
    if (locations.length === 0) return;

    let filtered = locations;

    // If there's a search term but no geocoded location, do text filtering
    if (searchTerm && !userLocation) {
      filtered = locations.filter(location =>
        location.site.toLowerCase().includes(searchTerm.toLowerCase()) ||
        location.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
        location.postcode.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    // If we have a user location (geocoded search), show ALL locations sorted by distance
    else if (userLocation) {
      filtered = sortLocationsByDistance(locations, userLocation.lat, userLocation.lng);
    }

    setFilteredLocations(filtered);
  }, [locations, searchTerm, userLocation]);

  const handleResize = () => {
    const mobile = window.innerWidth < 768;
    setIsMobile(mobile);
  };

  const loadLocationData = async () => {
    try {
      setLoading(true);
      const data = await loadCSVData();
      console.log('Loaded data:', data); // Debug log
      setLocations(data);
      setError(null);
    } catch (err) {
      setError('Failed to load location data. Please check that Map_Data.csv is in the public folder.');
      console.error('Error loading locations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLocationSelect = (location) => {
  setSelectedLocation(location);
  
  if (mapRef.current) {
    const map = mapRef.current;
    
    // Force map to invalidate size first (in case container changed)
    map.invalidateSize();
    
    // Use flyTo for smoother, more reliable centering
    map.flyTo(
      [location.coordinates[0], location.coordinates[1]], 
      13, // Fixed zoom level
      {
        duration: 1.0, // Smooth 1 second animation
        easeLinearity: 0.1 // Very smooth easing
      }
    );
    
    // Close any existing popups first
    map.closePopup();
    
    // Open the popup after the map finishes flying
    setTimeout(() => {
      map.eachLayer((layer) => {
        if (layer instanceof L.Marker) {
          const markerLatLng = layer.getLatLng();
          if (Math.abs(markerLatLng.lat - location.coordinates[0]) < 0.0001 && 
              Math.abs(markerLatLng.lng - location.coordinates[1]) < 0.0001) {
            layer.openPopup();
          }
        }
      });
    }, 1100); // Wait for flyTo animation to complete
  }
};

  // Custom marker for service locations
const serviceLocationIcon = new L.Icon({
  iconUrl: '/mappin.svg',
  iconSize: [40, 52],
  iconAnchor: [20, 52], // Center horizontally, bottom of icon for precise positioning
  popupAnchor: [0, -52], // Popup appears above the icon
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  shadowSize: [41, 41],
  shadowAnchor: [12, 41]
});

  const handleMobileLocationClick = (location) => {
    // On mobile, clicking a location card should navigate directly to the location page
    if (isMobile) {
      const url = getLocationUrl(location.site);
      // Check if we're inside an iframe
      if (window.parent !== window) {
        window.parent.location.href = url;
      } else {
        window.location.href = url;
      }
    } else {
      // On desktop, just select the location (existing behavior)
      handleLocationSelect(location);
    }
  };

  const handleSearch = async (term) => {
    setSearchTerm(term);
    setSearchError(null);

    // If term is empty, reset to show all locations
    if (!term.trim()) {
      setUserLocation(null);
      return;
    }

    // If term is short, just do text filtering (don't geocode yet)
    if (term.length < 3) {
      setUserLocation(null); // Clear any previous geocoded location
      return;
    }

    // Try to geocode the search term
    setSearchLoading(true);
    try {
      const result = await geocodeInput(term);
      setUserLocation(result);

      // Center map on user location with better animation
      if (mapRef.current) {
        mapRef.current.setView([result.lat, result.lng], 10, {
          animate: true,
          duration: 0.8
        });
      }

      console.log(`Geocoded location: ${result.location}`);

    } catch (error) {
      console.error('Search failed:', error);
      setSearchError(error.message);
      setUserLocation(null);
    } finally {
      setSearchLoading(false);
    }
  };

  // Helper function to generate the location URL
  const getLocationUrl = (siteName) => {
    const urlFriendlyName = siteName.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    return `https://www.driven-auto.co.uk/locations/${urlFriendlyName}`;
  };

  const handleFindOutMore = (location) => {
    const url = getLocationUrl(location.site);
    
    // Check if we're inside an iframe
    if (window.parent !== window) {
      // We're in an iframe, change the parent window's URL
      window.parent.location.href = url;
    } else {
      // We're not in an iframe, change current window's URL
      window.location.href = url;
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setUserLocation(null);
    setSearchError(null);
  };

  const handleGetDirections = (location) => {
    // Create Google Maps URL with the location's coordinates
    const lat = location.coordinates[0];
    const lng = location.coordinates[1];
    
    // Google Maps URL format for directions
    const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
    
    // Open in new tab/window
    window.open(googleMapsUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="spinner"></div>
          <p>Loading locations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-content">
          <div className="error-icon">⚠️</div>
          <h2>Error Loading Data</h2>
          <p>{error}</p>
          <button onClick={loadLocationData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Mobile view - List with optional details
  if (isMobile) {
    return (
      <div className="mobile-app-container">
        {/* Header */}
        <div className="mobile-header">
          <h1>Our locations</h1>
          <p className="mobile-description">
            Enter your postcode to find a same-day vehicle repair near you.
          </p>
          
          <SearchBar 
            searchTerm={searchTerm} 
            onSearch={handleSearch}
            placeholder="Postcode or Location"
            loading={searchLoading}
            onClear={clearSearch}
          />
          
          {searchError && (
            <div className="search-error">
              {searchError}
            </div>
          )}
          
          <p className="results-count">
            {filteredLocations.length} location{filteredLocations.length !== 1 ? 's' : ''} found
          </p>
        </div>

        {/* Mobile Locations List */}
        <div className="mobile-locations-list">
          {filteredLocations.length === 0 ? (
            <div className="no-results">
              {searchTerm 
                ? `No locations found matching "${searchTerm}"`
                : "Loading locations..."
              }
            </div>
          ) : (
            filteredLocations.map((location) => (
              <LocationCard
                key={location.id}
                location={location}
                isSelected={selectedLocation?.id === location.id}
                onClick={() => handleMobileLocationClick(location)}
                isMobile={true}
                distance={location.distance ? formatDistance(location.distance) : null}
              />
            ))
          )}
        </div>

        {/* Selected Location Details for Mobile */}
        {selectedLocation && (
          <LocationDetails 
            location={selectedLocation} 
            onClose={() => setSelectedLocation(null)}
            onFindOutMore={handleFindOutMore}
            isMobile={true}
            CloseIcon={XMarkIcon}
          />
        )}
      </div>
    );
  }

  // Desktop view - Sidebar + Map
  return (
    <div className="app-container">
      {/* Sidebar */}
      <div className="sidebar sidebar-open">
        {/* Header */}
        <div className="sidebar-header">
          <h1>Our locations</h1>
          <p className="sidebar-description">
            Enter your postcode to find a speedy repair near you.
          </p>
          
          <SearchBar 
            searchTerm={searchTerm} 
            onSearch={handleSearch}
            placeholder="Postcode or Location"
            loading={searchLoading}
            onClear={clearSearch}
          />
          
          {searchError && (
            <div className="search-error">
              {searchError}
            </div>
          )}
          
        </div>

        {/* Locations List */}
        <div className="locations-list">
          {filteredLocations.length === 0 ? (
            <div className="no-results">
              {searchTerm 
                ? `No locations found matching "${searchTerm}"`
                : locations.length === 0 
                ? "Loading locations..."
                : "No locations available"
              }
            </div>
          ) : (
            filteredLocations.map((location) => (
              <LocationCard
                key={location.id}
                location={location}
                isSelected={selectedLocation?.id === location.id}
                onClick={() => handleLocationSelect(location)}
                isMobile={false}
                distance={location.distance ? formatDistance(location.distance) : null}
              />
            ))
          )}
        </div>

      </div>

      {/* Map */}
      <div className="map-container">
        <MapContainer
          scrollWheelZoom={false}
          ref={mapRef}
          center={userLocation ? [userLocation.lat, userLocation.lng] : [52.4862, -1.8904]}
          zoom={userLocation ? 10 : 7}
          style={{ height: '100%', width: '100%' }}
          whenCreated={(mapInstance) => {
            mapRef.current = mapInstance;
            // Ensure map resizes properly when container changes
            setTimeout(() => {
              mapInstance.invalidateSize();
            }, 100);
          }}
        >
          {CARTO_KEY ? (
            <TileLayer
              url={`https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`}
              subdomains="abcd"
              maxZoom={20}
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            />
          ) : (
            <TileLayer
              url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={19}
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
          )}
          
          {/* User location marker */}
          {userLocation && (
            <Marker
              position={[userLocation.lat, userLocation.lng]}
              icon={userLocationIcon}
            >
              <Popup>
                <div className="map-popup">
                  <h3>📍 Your Location</h3>
                  <p>{userLocation.location}</p>
                </div>
              </Popup>
            </Marker>
          )}
          
          {/* Service location markers with custom popup */}
          {filteredLocations.map((location) => (
            <Marker
              key={location.id}
              position={location.coordinates}
              icon={serviceLocationIcon} // Add this line
              eventHandlers={{
                click: () => handleLocationSelect(location),
              }}
            >
             <Popup maxWidth={280} minWidth={240} closeButton={false}>
                <div className="map-popup">
                  <div className="popup-content">
                    <PopupImage location={location} />
                    <div className="popup-location-info">
                      <h3 className="popup-site-name">{getPopupTitle(location)}</h3>
                      <p className="popup-site-address">{location.address}</p>
                    </div>
                    
                    <div className="popup-actions">
                      <button 
                        className="popup-button-primary"
                        onClick={() => handleFindOutMore(location)}
                      >
                        Find out more
                      </button>
                      <button 
                        className="popup-button-secondary"
                        onClick={() => handleGetDirections(location)}
                      >
                        Get directions
                      </button>
                    </div>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* Selected Location Details for Desktop */}
      {selectedLocation && (
        <LocationDetails 
          location={selectedLocation} 
          onClose={() => setSelectedLocation(null)}
          onFindOutMore={handleFindOutMore}
          isMobile={false}
          CloseIcon={XMarkIcon}
        />
      )}
    </div>
  );
};

export default MapApp;